import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Search, User, ChevronDown, ChevronUp } from 'lucide-react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { LoadingSpinner } from '@/components/ui/spinner';
// Types
interface NameHistory {
  firstName?: string | null;
  lastName?: string | null;
}

interface Participant {
  id: string | number | null;
  nameHistory?: NameHistory[];
  programs?: Array<string | number>;
  gender?: string | null;
  mrun?: string | null;
  fin?: string | null;
  champid?: string | null;
}

const SEARCH_PARTICIPANTS = gql`
  query searchParticipants($search: String!) {
    searchParticipants(search: $search) {
      id
      nameHistory {
        firstName
        lastName
      }
      programs
      gender
      mrun
      fin
      champid
    }
  }
`;

type ProgramConfigType = {
  [key: string]: { 
    max: number;
    classes: string;
  }
};

const PROGRAM_CONFIG: ProgramConfigType = {
  MIST: { max: 20000000, classes: 'bg-blue-100 text-blue-800' },
  ODR: { max: 30000000, classes: 'bg-green-100 text-green-800' },
  PRWM: { max: 40000000, classes: 'bg-purple-100 text-purple-800' },
  FIST: { max: 60000000, classes: 'bg-orange-100 text-orange-800' },
  DSH: { max: 70000000, classes: 'bg-red-100 text-red-800' },
  LEAD: { max: 80000000, classes: 'bg-yellow-100 text-yellow-800' },
};


const formatSearchValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map(formatSearchValue).filter(Boolean).join(', ');
  }

  return String(value);
};

const ProgramBadge = ({ id }: { id: string | number }) => {
  const numericId = parseInt(formatSearchValue(id), 10);
  
  for (const [program, { max, classes }] of Object.entries(PROGRAM_CONFIG)) {
    if (numericId < max) {
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-0 text-xs font-medium ${classes}`}>
          {program}
        </span>
      );
    }
  }
  
  return null;
};

const buildParticipantNamesCsv = (nameHistory?: NameHistory[]) => {
  if (!Array.isArray(nameHistory) || nameHistory.length === 0) {
    return '';
  }

  const names = nameHistory
    .map((name) => `${name.firstName || ''} ${name.lastName || ''}`.trim())
    .filter(Boolean);

  return Array.from(new Set(names)).join(', ');
};

const truncateValue = (value: unknown, max = 70) => {
  const formattedValue = formatSearchValue(value);

  if (formattedValue.length <= max) {
    return {
      display: formattedValue,
      truncated: false,
    };
  }

  return {
    display: formattedValue.slice(0, max).trimEnd(),
    truncated: true,
  };
};

export const SearchComponent = ({ onClose: _onClose, onSearch, searchQuery }: { onClose: () => void, onSearch: (search: string) => void , searchQuery?: string}) => {
  const [searchResults, setSearchResults] = useState<Participant[]>([]);
  const [hasSearched, setHasSearched] = useState(Boolean(searchQuery?.trim()));
  const [expandedParticipantId, setExpandedParticipantId] = useState<string | null>(null);

  const [searchParticipants, { loading }] = useLazyQuery(SEARCH_PARTICIPANTS);

  useEffect(() => {
    const normalizedQuery = searchQuery?.trim();

    if (!normalizedQuery) {
      setHasSearched(false);
      setSearchResults([]);
      setExpandedParticipantId(null);
      return;
    }

    setHasSearched(true);
    searchParticipants({ variables: { search: normalizedQuery }, onCompleted: (data) => {
      if (data?.searchParticipants) {
        setSearchResults(data.searchParticipants);
        setExpandedParticipantId(null);
      }
    }});
  }, [searchParticipants, searchQuery]);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {loading ? (
          <div className="app-page-shell flex h-full min-h-[220px] items-center justify-center px-6">
            <LoadingSpinner />
          </div>
        ) : !hasSearched ? (
          <div className="app-page-shell flex h-full min-h-[220px] items-center justify-center border-dashed bg-[var(--surface-muted)] px-6 text-center">
            <div className="space-y-2">
              <Search className="mx-auto h-5 w-5 text-[var(--neutral-gray-70)]" />
              <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Use the header search</div>
              <p className="text-xs text-[var(--neutral-gray-80)]">
                Search for a participant from the header to load results here.
              </p>
            </div>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="app-page-shell flex h-full min-h-[220px] items-center justify-center border-dashed bg-[var(--surface-muted)] px-6 text-center">
            <div className="space-y-2">
              <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">No participants found</div>
              <p className="text-xs text-[var(--neutral-gray-80)]">
                Try a different name, CID, MRUN, FIN, or ChampID search.
              </p>
            </div>
          </div>
        ) : (
          <div className="app-page-shell h-full overflow-auto">
            <Table className="min-w-full table-fixed">
              <TableHeader>
                <TableRow className="bg-[var(--surface-muted)]">
                  <TableHead className="w-[64px] px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    Programs
                  </TableHead>
                  <TableHead className="w-[38%] px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    Participant
                  </TableHead>
                  <TableHead className="w-[64px] px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    Gender
                  </TableHead>
                  <TableHead className="w-[96px] px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    MRUN
                  </TableHead>
                  <TableHead className="w-[96px] px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    FIN
                  </TableHead>
                  <TableHead className="w-[96px] px-2 py-2.5 pr-4 text-right text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    ChampID
                  </TableHead>
                </TableRow>
              </TableHeader>
              
              <TableBody>
                {searchResults.map((result, resultIndex) => {
                  const participantId = formatSearchValue(result.id);
                  const namesCsv = buildParticipantNamesCsv(result.nameHistory);
                  const participantDisplay = namesCsv || participantId || '-';
                  const isExpanded = expandedParticipantId === participantId;
                  const truncatedName = truncateValue(participantDisplay, 60);
                  const visiblePrograms = Array.isArray(result.programs) ? result.programs.slice(0, 4) : [];
                  const hiddenProgramCount = Array.isArray(result.programs) && result.programs.length > 4 ? result.programs.length - 4 : 0;

                  return (
                    <React.Fragment key={participantId || `participant-${resultIndex}`}>
                      <TableRow
                        onClick={() => participantId && onSearch(participantId)}
                        className="cursor-pointer hover:bg-[var(--neutral-gray-10)]"
                      >
                        <TableCell className="px-2 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {visiblePrograms.map((p) => (
                              <ProgramBadge key={p} id={p} />
                            ))}
                            {hiddenProgramCount > 0 ? (
                              <span className="inline-flex items-center rounded-full bg-[var(--neutral-gray-20)] px-2 py-0 text-xs font-medium text-[var(--neutral-gray-80)]">
                                +{hiddenProgramCount}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>

                        <TableCell className="px-3 py-3">
                          <div className="flex min-w-0 items-center">
                            <div className="flex-shrink-0">
                              <User className="h-7 w-7 text-[var(--neutral-gray-70)]" />
                            </div>
                            <div className="ml-3 min-w-0 flex flex-1 items-center gap-1">
                              <div
                                className="inline-flex max-w-[60ch] min-w-0 items-center gap-1 text-left text-sm text-[var(--azure-primary)]"
                                title={namesCsv || participantId}
                              >
                                <span className="truncate">{truncatedName.display}</span>
                                {truncatedName.truncated ? (
                                  <span className="shrink-0 text-xs font-semibold text-[var(--azure-primary)]">...</span>
                                ) : null}
                              </div>
                              {truncatedName.truncated ? (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setExpandedParticipantId((current) => current === participantId ? null : participantId);
                                  }}
                                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--azure-primary)] hover:bg-[var(--neutral-gray-20)]"
                                  aria-label={isExpanded ? 'Hide all names' : 'Show all names'}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-3 w-3" />
                                  ) : (
                                    <ChevronDown className="h-3 w-3" />
                                  )}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="px-2 py-3 whitespace-nowrap text-sm text-[var(--neutral-gray-80)]">
                          {result.gender || '-'}
                        </TableCell>
                        
                        <TableCell className="px-2 py-3 whitespace-nowrap text-right text-sm tabular-nums text-[var(--neutral-gray-80)]">
                          {result.mrun || '-'}
                        </TableCell>
                        
                        <TableCell className="px-2 py-3 whitespace-nowrap text-right text-sm tabular-nums text-[var(--neutral-gray-80)]">
                          {result.fin || '-'}
                        </TableCell>
                        
                        <TableCell className="px-2 py-3 pr-4 whitespace-nowrap text-right text-sm tabular-nums text-[var(--neutral-gray-80)]">
                          {result.champid || '-'}
                        </TableCell>
                      </TableRow>

                      {isExpanded ? (
                        <TableRow className="bg-[var(--surface-muted)]">
                          <TableCell colSpan={6} className="px-4 py-2.5 text-xs text-[var(--neutral-gray-90)]">
                            <span className="font-semibold text-[var(--neutral-gray-120)]">All names:</span>{' '}
                            {namesCsv || 'No alternate names'}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      </div>  
  );
};
